import { useMemo, useState } from "react";
import type { WorkspaceEntry } from "../../services/file.service";
import { Input } from "../ui";

/**
 * Árvore de arquivos do workspace. Recebe a lista plana da API e monta a
 * hierarquia em memória — nenhuma leitura de filesystem no navegador.
 */

interface TreeNode {
  name: string;
  path: string;
  type: "file" | "directory";
  children: TreeNode[];
}

function buildTree(entries: WorkspaceEntry[]): TreeNode[] {
  const root: TreeNode = { name: "", path: "", type: "directory", children: [] };

  const sorted = [...entries].sort((a, b) => a.path.localeCompare(b.path));

  for (const entry of sorted) {
    const segments = entry.path.split("/");
    let current = root;

    segments.forEach((segment, index) => {
      const isLeaf = index === segments.length - 1;
      const path = segments.slice(0, index + 1).join("/");
      let child = current.children.find((node) => node.name === segment);

      if (!child) {
        child = {
          name: segment,
          path,
          type: isLeaf ? entry.type : "directory",
          children: [],
        };
        current.children.push(child);
      }

      current = child;
    });
  }

  const order = (nodes: TreeNode[]): TreeNode[] =>
    nodes
      .map((node) => ({ ...node, children: order(node.children) }))
      .sort((a, b) => {
        if (a.type !== b.type) return a.type === "directory" ? -1 : 1;
        return a.name.localeCompare(b.name);
      });

  return order(root.children);
}

export interface FileTreeProps {
  entries: WorkspaceEntry[];
  activePath: string | null;
  dirtyPaths?: string[];
  onOpen: (path: string) => void;
  onCreateFile?: (parent: string) => void;
  onCreateDirectory?: (parent: string) => void;
  onRename?: (path: string) => void;
  onDelete?: (path: string) => void;
}

export default function FileTree({
  entries,
  activePath,
  dirtyPaths = [],
  onOpen,
  onCreateFile,
  onCreateDirectory,
  onRename,
  onDelete,
}: FileTreeProps) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const tree = useMemo(() => buildTree(entries), [entries]);

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return null;
    return entries.filter((entry) => entry.type === "file" && entry.path.toLowerCase().includes(term)).slice(0, 200);
  }, [entries, search]);

  function toggle(path: string) {
    setExpanded((current) => ({ ...current, [path]: !current[path] }));
  }

  function renderNodes(nodes: TreeNode[], depth: number) {
    return nodes.map((node) => {
      const isOpen = expanded[node.path] ?? depth === 0;
      const isActive = node.path === activePath;
      const isDirty = dirtyPaths.includes(node.path);

      return (
        <li key={node.path}>
          <div
            className={`group flex items-center gap-1 rounded px-1 ${
              isActive ? "bg-brand/15 text-brand" : "text-ink-muted hover:bg-surface-raised/70"
            }`}
            style={{ paddingLeft: `${depth * 12 + 4}px` }}
          >
            <button
              type="button"
              onClick={() => (node.type === "directory" ? toggle(node.path) : onOpen(node.path))}
              className="flex-1 truncate py-1 text-left font-mono text-[11px]"
              title={node.path}
            >
              {node.type === "directory" ? (isOpen ? "▾ " : "▸ ") : "  "}
              {node.name}
              {isDirty && <span className="ml-1 text-warn">•</span>}
            </button>

            <span className="hidden shrink-0 gap-1 group-hover:flex">
              {node.type === "directory" && onCreateFile && (
                <IconAction label="Novo arquivo" onClick={() => onCreateFile(node.path)}>
                  +
                </IconAction>
              )}
              {node.type === "directory" && onCreateDirectory && (
                <IconAction label="Nova pasta" onClick={() => onCreateDirectory(node.path)}>
                  ⌸
                </IconAction>
              )}
              {onRename && (
                <IconAction label="Renomear" onClick={() => onRename(node.path)}>
                  ✎
                </IconAction>
              )}
              {onDelete && (
                <IconAction label="Excluir" onClick={() => onDelete(node.path)}>
                  ✕
                </IconAction>
              )}
            </span>
          </div>

          {node.type === "directory" && isOpen && node.children.length > 0 && (
            <ul>{renderNodes(node.children, depth + 1)}</ul>
          )}
        </li>
      );
    });
  }

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Input
            value={search}
            placeholder="Buscar arquivo…"
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {onCreateFile && (
          <IconAction label="Novo arquivo na raiz" onClick={() => onCreateFile("")}>
            +
          </IconAction>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {matches ? (
          <ul className="space-y-0.5">
            {matches.length === 0 && <li className="px-1 text-xs text-ink-muted">Nada encontrado.</li>}
            {matches.map((entry) => (
              <li key={entry.path}>
                <button
                  type="button"
                  onClick={() => onOpen(entry.path)}
                  className={`w-full truncate rounded px-1 py-1 text-left font-mono text-[11px] ${
                    entry.path === activePath ? "bg-brand/15 text-brand" : "text-ink-muted hover:bg-surface-raised/70"
                  }`}
                  title={entry.path}
                >
                  {entry.path}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <ul>{renderNodes(tree, 0)}</ul>
        )}
      </div>
    </div>
  );
}

function IconAction({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="rounded px-1 text-[11px] text-ink-muted hover:bg-surface-raised hover:text-ink"
    >
      {children}
    </button>
  );
}
